import { useState } from "react";
import { Check, ChevronDown, Loader2, User } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { DateQuickPicks } from "@/components/ui/date-quick-picks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { useRaiseIssue, useRaiseIssues } from "@/hooks/use-issue-mutations";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProjectPeople } from "@/hooks/use-project-people";
import {
  issueTitleOrPlaceholder,
  personInitials,
  personName,
} from "@/lib/issues";
import { cn } from "@/lib/utils";
import type { ProjectIssue } from "@/types/api";

/** A record in a batch raise, with the remarks its title falls back to. */
export interface BatchRaiseRecord {
  id: string;
  remarks?: string;
}

interface BaseProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SingleRaiseProps {
  recordId: string;
  /**
   * Seeds the title. The record's remarks are usually already a description of
   * the problem, so retyping them is busywork — but it stays editable, because
   * a record's remarks and an issue's title are not always the same sentence.
   */
  defaultTitle?: string;
  onRaised?: (issue: ProjectIssue) => void;
  records?: never;
}

interface BatchRaiseProps {
  /**
   * One issue is raised per record. Read once when the sheet opens: the host's
   * selection may change underneath a running batch, and the batch must not.
   */
  records: BatchRaiseRecord[];
  /** Selected photos left out because they have no server id yet. */
  skippedCount?: number;
  /**
   * Called after each attempt with the records that were raised in it, so the
   * host can stop offering them. Fires on a partial batch too — the sheet may
   * be closed before the rest are retried, and those issues exist regardless.
   */
  onBatchRaised?: (recordIds: string[]) => void;
  /** Called once every record in the batch has been raised. */
  onBatchComplete?: (raisedCount: number) => void;
  recordId?: never;
}

type RaiseIssueSheetProps = BaseProps & (SingleRaiseProps | BatchRaiseProps);

export function RaiseIssueSheet(props: RaiseIssueSheetProps) {
  const { projectId, open, onOpenChange } = props;
  // A batch cannot be dismissed while it runs — closing halfway would leave
  // the user not knowing which issues exist. See [[raise-issue-sheet.spec]].
  const [isBusy, setIsBusy] = useState(false);
  const close = () => onOpenChange(false);

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next && isBusy) return;
        onOpenChange(next);
      }}
      dismissible={!isBusy}
    >
      <DrawerContent className="max-h-[90vh]">
        {/* Keyed on open so each raise starts from a clean form — see
            [[issue-update-sheet.spec]] for why this is a remount, not an
            effect. */}
        {props.records ? (
          <BatchRaiseForm
            key={open ? "open" : "closed"}
            projectId={projectId}
            records={props.records}
            skippedCount={props.skippedCount ?? 0}
            onClose={close}
            onBusyChange={setIsBusy}
            onBatchRaised={props.onBatchRaised}
            onBatchComplete={props.onBatchComplete}
          />
        ) : (
          <RaiseForm
            key={open ? "open" : "closed"}
            projectId={projectId}
            recordId={props.recordId}
            defaultTitle={props.defaultTitle}
            onClose={close}
            onRaised={props.onRaised}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
}

interface RaiseFormProps {
  projectId: string;
  recordId: string;
  defaultTitle?: string;
  onClose: () => void;
  onRaised?: (issue: ProjectIssue) => void;
}

function RaiseForm({
  projectId,
  recordId,
  defaultTitle,
  onClose,
  onRaised,
}: RaiseFormProps) {
  const [title, setTitle] = useState(defaultTitle?.trim() ?? "");
  const [fields, setFields] = useState(EMPTY_SHARED_FIELDS);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Minted once per open (the form is keyed on `open`, so a fresh sheet gets a
  // fresh id). Retrying the same sheet reuses it, which is exactly what makes
  // the retry idempotent server-side.
  const [clientIssueUuid] = useState(() => crypto.randomUUID());

  const raiseIssue = useRaiseIssue();

  const submit = () => {
    if (!title.trim()) {
      setErrorMessage("Give the issue a title.");
      return;
    }
    setErrorMessage(null);
    raiseIssue.mutate(
      {
        projectId,
        recordId,
        title: title.trim(),
        description: fields.description.trim(),
        assignedToId: fields.assignedTo,
        dueDate: fields.dueDate || null,
        clientIssueUuid,
      },
      {
        onSuccess: (issue) => {
          onRaised?.(issue);
          onClose();
        },
        onError: () =>
          setErrorMessage(
            "Couldn't raise that issue. Check your connection and try again.",
          ),
      },
    );
  };

  return (
    <>
      <DrawerHeader>
        <DrawerTitle>Raise issue</DrawerTitle>
      </DrawerHeader>

      <div className="flex flex-col gap-5 overflow-y-auto px-4 pb-4">
        <section className="flex flex-col gap-2">
          <label htmlFor="issue-title" className="text-[13px] font-medium">
            Title
          </label>
          <Input
            id="issue-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs fixing?"
          />
        </section>

        <SharedIssueFields
          projectId={projectId}
          value={fields}
          onChange={setFields}
        />

        {errorMessage && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage}
          </p>
        )}
      </div>

      <div className="pb-sheet-actions flex gap-2 border-t border-border p-4">
        <Button
          variant="outline"
          className="basis-[38%]"
          onClick={onClose}
          disabled={raiseIssue.isPending}
        >
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={submit}
          disabled={raiseIssue.isPending}
        >
          {raiseIssue.isPending ? "Raising…" : "Raise issue"}
        </Button>
      </div>
    </>
  );
}

interface BatchRaiseFormProps {
  projectId: string;
  records: BatchRaiseRecord[];
  skippedCount: number;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
  onBatchRaised?: (recordIds: string[]) => void;
  onBatchComplete?: (raisedCount: number) => void;
}

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/**
 * One issue per record, sharing assignee, due date and description.
 *
 * The title is optional here, unlike a single raise: typed, it goes on every
 * issue; blank, each issue takes its own photo's remarks and then the
 * placeholder. See [[raise-issue-sheet.spec]] § Batch mode.
 */
function BatchRaiseForm({
  projectId,
  records,
  skippedCount,
  onClose,
  onBusyChange,
  onBatchRaised,
  onBatchComplete,
}: BatchRaiseFormProps) {
  // Snapshotted at open — see `BatchRaiseProps.records`.
  const [batch] = useState(records);
  // One idempotency key per record, minted once per opening and reused on
  // every retry, so an issue whose response was lost comes back as itself.
  const [uuids] = useState(
    () => new Map(batch.map((record) => [record.id, crypto.randomUUID()])),
  );
  const [title, setTitle] = useState("");
  const [fields, setFields] = useState(EMPTY_SHARED_FIELDS);
  // Records still without an issue. Shrinks as attempts land; a retry sends
  // exactly these.
  const [remainingIds, setRemainingIds] = useState(() =>
    batch.map((record) => record.id),
  );
  const [hasAttempted, setHasAttempted] = useState(false);
  const [progress, setProgress] = useState<{
    settled: number;
    total: number;
  } | null>(null);

  const raiseIssues = useRaiseIssues();
  const isRaising = raiseIssues.isPending;
  const raisedCount = batch.length - remainingIds.length;
  const failedCount = hasAttempted ? remainingIds.length : 0;

  const submit = () => {
    const toRaise = batch.filter((record) => remainingIds.includes(record.id));
    if (toRaise.length === 0) return;
    const typedTitle = title.trim();

    setHasAttempted(true);
    setProgress({ settled: 0, total: toRaise.length });
    onBusyChange(true);

    raiseIssues.mutate(
      {
        projectId,
        issues: toRaise.map((record) => ({
          recordId: record.id,
          // A typed title wins for the whole batch; otherwise the photo's own
          // remarks, then the placeholder — never blank on the wire.
          title: typedTitle || issueTitleOrPlaceholder(record.remarks),
          clientIssueUuid: uuids.get(record.id) ?? crypto.randomUUID(),
        })),
        description: fields.description.trim(),
        assignedToId: fields.assignedTo,
        dueDate: fields.dueDate || null,
        onProgress: (settled) =>
          setProgress({ settled, total: toRaise.length }),
      },
      {
        onSuccess: ({ raised, failed }) => {
          const raisedIds = raised.map((entry) => entry.recordId);
          if (raisedIds.length > 0) onBatchRaised?.(raisedIds);
          const stillFailing = failed.map((entry) => entry.recordId);
          setRemainingIds(stillFailing);
          if (stillFailing.length === 0) {
            onBusyChange(false);
            onBatchComplete?.(batch.length);
            onClose();
          }
        },
        onSettled: () => {
          onBusyChange(false);
          setProgress(null);
        },
      },
    );
  };

  const errorMessage =
    failedCount === 0
      ? null
      : raisedCount === 0
        ? "Couldn't raise these issues — check your connection and try again."
        : `Raised ${raisedCount} of ${batch.length}. ${failedCount} couldn't be raised — check your connection and try again.`;

  const primaryLabel = isRaising
    ? "Raising…"
    : failedCount > 0
      ? `Retry ${failedCount}`
      : `Raise ${plural(batch.length, "issue", "issues")}`;

  const inFlight = progress
    ? Math.min(progress.settled + 1, progress.total)
    : 0;

  return (
    <>
      <DrawerHeader>
        <DrawerTitle>
          Raise {plural(batch.length, "issue", "issues")}
        </DrawerTitle>
        {skippedCount > 0 && (
          <p className="text-sm text-muted-foreground">
            {plural(skippedCount, "photo", "photos")} still uploading{" "}
            {skippedCount === 1 ? "was" : "were"} left out.
          </p>
        )}
      </DrawerHeader>

      <div className="flex flex-col gap-5 overflow-y-auto px-4 pb-4">
        {/* One switch for every control, the date picker included — the form
            is read at submit, so an edit mid-batch would only confuse which
            values the running issues carry. The date picker and quick picks
            carry no :disabled styling of their own, hence the button rule. */}
        <fieldset
          disabled={isRaising}
          className="m-0 flex min-w-0 flex-col gap-5 border-0 p-0 [&_button:disabled]:opacity-50"
        >
          <section className="flex flex-col gap-2">
            <label htmlFor="issue-title" className="text-[13px] font-medium">
              Title
            </label>
            <Input
              id="issue-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Same title for every issue"
              aria-describedby="issue-title-hint"
            />
            <p
              id="issue-title-hint"
              className="text-xs text-muted-foreground"
            >
              Leave blank to use each photo&apos;s remarks, or
              &ldquo;Issue&rdquo; if it has none.
            </p>
          </section>

          <SharedIssueFields
            projectId={projectId}
            value={fields}
            onChange={setFields}
          />
        </fieldset>

        {progress && (
          <div
            role="status"
            aria-live="polite"
            className="flex flex-col gap-2"
          >
            <Progress
              value={(progress.settled / progress.total) * 100}
              aria-label="Raising issues"
              className="h-1.5"
            />
            <p className="text-sm text-muted-foreground">
              Raising {inFlight} of {progress.total}…
            </p>
          </div>
        )}

        {errorMessage && !isRaising && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage}
          </p>
        )}
      </div>

      <div className="pb-sheet-actions flex gap-2 border-t border-border p-4">
        <Button
          variant="outline"
          className="basis-[38%]"
          onClick={onClose}
          disabled={isRaising}
        >
          Cancel
        </Button>
        <Button className="flex-1" onClick={submit} disabled={isRaising}>
          {isRaising && (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          )}
          {primaryLabel}
        </Button>
      </div>
    </>
  );
}

interface SharedFieldsValue {
  assignedTo: string | null;
  dueDate: string;
  description: string;
}

const EMPTY_SHARED_FIELDS: SharedFieldsValue = {
  assignedTo: null,
  dueDate: "",
  description: "",
};

interface SharedIssueFieldsProps {
  projectId: string;
  value: SharedFieldsValue;
  onChange: (value: SharedFieldsValue) => void;
}

/**
 * Assign to, Due date and Description — identical for one issue or a batch,
 * so both forms render this rather than each carrying a copy.
 */
function SharedIssueFields({
  projectId,
  value,
  onChange,
}: SharedIssueFieldsProps) {
  const { people } = useProjectPeople(projectId);
  const { data: currentUser } = useCurrentUser();
  const { assignedTo, dueDate, description } = value;
  const set = (patch: Partial<SharedFieldsValue>) =>
    onChange({ ...value, ...patch });

  const selectedPerson =
    people.find((person) => person.id === assignedTo) ?? null;

  return (
    <>
      <section className="flex flex-col gap-2">
        <span className="text-[13px] font-medium">Assign to</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Assign to"
              className="flex h-[46px] w-full items-center justify-between gap-2.5 rounded-lg border border-border bg-card px-3 text-sm disabled:opacity-50"
            >
              <span className="flex min-w-0 items-center gap-2.5">
                {selectedPerson ? (
                  <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-tertiary text-[11px] font-semibold text-tertiary-foreground">
                    {personInitials(selectedPerson)}
                  </span>
                ) : (
                  <User
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
                <span
                  className={cn(
                    "truncate",
                    !selectedPerson && "text-muted-foreground",
                  )}
                >
                  {selectedPerson
                    ? selectedPerson.id === currentUser?.id
                      ? `${personName(selectedPerson)} (me)`
                      : personName(selectedPerson)
                    : "Nobody yet"}
                </span>
              </span>
              <ChevronDown
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-[var(--radix-dropdown-menu-trigger-width)]"
          >
            <DropdownMenuItem onSelect={() => set({ assignedTo: null })}>
              <span className="flex-1">Nobody yet</span>
              {assignedTo === null && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
            {people.map((person) => (
              <DropdownMenuItem
                key={person.id}
                onSelect={() => set({ assignedTo: person.id })}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tertiary text-[10px] font-semibold text-tertiary-foreground">
                  {personInitials(person)}
                </span>
                <span className="flex-1 truncate">
                  {person.id === currentUser?.id
                    ? `${personName(person)} (me)`
                    : personName(person)}
                </span>
                {assignedTo === person.id && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </section>

      <section className="flex flex-col gap-2">
        <span className="text-[13px] font-medium">Due date</span>
        <DatePicker
          value={dueDate || null}
          onChange={(next) => set({ dueDate: next ?? "" })}
          aria-label="Due date"
        />
        {/* Unlike the update sheet's target date, these set the issue's real
            `due_date` — the thing overdue badges and sorting run off. Same
            three offsets, because a deadline a week out is the common answer
            and a calendar is three taps to say it. */}
        <DateQuickPicks
          value={dueDate || null}
          onChange={(next) => set({ dueDate: next })}
        />
      </section>

      <section className="flex flex-col gap-2">
        <label htmlFor="issue-description" className="text-[13px] font-medium">
          Description
        </label>
        <Textarea
          id="issue-description"
          value={description}
          onChange={(event) => set({ description: event.target.value })}
          rows={3}
        />
      </section>
    </>
  );
}
